import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Calculator, DollarSign, Target, Calendar, Gift, AlertCircle, CheckCircle, HelpCircle, PieChart, Briefcase, Building2, Landmark } from 'lucide-react';
import { updatePageSEO } from '../utils/seo';

const USRetirementCalculatorPage = () => {
  const navigate = useNavigate();

  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'US Retirement Calculator - PlanLens.ai | 401k, IRA & Social Security Planner',
      description: 'Free US retirement calculator. Plan your 401k, IRA, Roth IRA contributions. Calculate Social Security benefits, estimate retirement savings, and optimize withdrawal strategies.',
      canonical: 'https://planlens.ai/calculators/us-retirement-calculator'
    });
  }, []);

  // Calculator state
  const [age, setAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(67);
  const [socialSecurityAge, setSocialSecurityAge] = useState(67);

  // 401(k)
  const [current401k, setCurrent401k] = useState(50000);
  const [monthly401kEmployee, setMonthly401kEmployee] = useState(500);
  const [monthly401kEmployer, setMonthly401kEmployer] = useState(250);

  // Traditional IRA
  const [currentTraditionalIRA, setCurrentTraditionalIRA] = useState(20000);
  const [monthlyTraditionalIRA, setMonthlyTraditionalIRA] = useState(200);

  // Roth IRA
  const [currentRothIRA, setCurrentRothIRA] = useState(15000);
  const [monthlyRothIRA, setMonthlyRothIRA] = useState(200);

  // Pension (Defined Benefit)
  const [hasPension, setHasPension] = useState(false);
  const [pensionAnnual, setPensionAnnual] = useState(10000);

  // Social Security
  const [includeSocialSecurity, setIncludeSocialSecurity] = useState(true);
  const [estimatedSocialSecurity, setEstimatedSocialSecurity] = useState(30000);

  // Investment assumptions
  const [expectedReturn, setExpectedReturn] = useState(7);

  // US Social Security 2024 - Average benefit
  const AVERAGE_SOCIAL_SECURITY_ANNUAL = 22000; // Average ~$1,907/month * 12

  // Calculate comprehensive retirement projections
  const calculateRetirement = () => {
    const yearsToRetirement = retirementAge - age;
    const returnRate = expectedReturn / 100;

    // ===== 401(k) Calculation =====
    const total401kAnnualContribution = (monthly401kEmployee + monthly401kEmployer) * 12;

    let val401k = current401k;
    let total401kContributed = 0;
    let total401kEmployer = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      val401k = val401k * (1 + returnRate) + total401kAnnualContribution;
      total401kContributed += monthly401kEmployee * 12;
      total401kEmployer += monthly401kEmployer * 12;
    }

    // ===== Traditional IRA Calculation =====
    const traditionalIRAAnnualContribution = monthlyTraditionalIRA * 12;

    let valTraditionalIRA = currentTraditionalIRA;
    let totalTraditionalIRAContributed = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      valTraditionalIRA = valTraditionalIRA * (1 + returnRate) + traditionalIRAAnnualContribution;
      totalTraditionalIRAContributed += traditionalIRAAnnualContribution;
    }

    // ===== Roth IRA Calculation =====
    const rothIRAAnnualContribution = monthlyRothIRA * 12;

    let valRothIRA = currentRothIRA;
    let totalRothIRAContributed = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      valRothIRA = valRothIRA * (1 + returnRate) + rothIRAAnnualContribution;
      totalRothIRAContributed += rothIRAAnnualContribution;
    }

    // ===== Total Pre-Tax and Roth Balances =====
    const totalPreTax = val401k + valTraditionalIRA;
    const totalRoth = valRothIRA;
    const totalRetirementSavings = totalPreTax + totalRoth;

    // Estimate annual drawdown (4% rule on pre-tax accounts)
    const preTaxDrawdown = totalPreTax * 0.04;
    const rothDrawdown = totalRoth * 0.04; // Tax-free withdrawals

    // ===== Total Annual Retirement Income =====
    // Calculate income WITHOUT Social Security (for early retirement)
    let annualIncomeBeforeSocialSecurity = preTaxDrawdown + rothDrawdown;

    // Add pension if included
    if (hasPension) {
      annualIncomeBeforeSocialSecurity += pensionAnnual;
    }

    const monthlyIncomeBeforeSocialSecurity = annualIncomeBeforeSocialSecurity / 12;

    // Calculate income WITH Social Security (from Social Security Age onwards)
    let totalAnnualIncome = annualIncomeBeforeSocialSecurity;

    // Add Social Security if included (only from Social Security Age)
    const socialSecurityIncome = includeSocialSecurity ? estimatedSocialSecurity : 0;
    totalAnnualIncome += socialSecurityIncome;

    const monthlyIncome = totalAnnualIncome / 12;

    // ===== Total Contributions =====
    const totalPersonalContributions = total401kContributed + totalTraditionalIRAContributed + totalRothIRAContributed;
    const totalEmployerContributions = total401kEmployer;

    return {
      // 401(k)
      val401k: Math.round(val401k),
      total401kContributed: Math.round(total401kContributed),
      total401kEmployer: Math.round(total401kEmployer),

      // Traditional IRA
      valTraditionalIRA: Math.round(valTraditionalIRA),
      totalTraditionalIRAContributed: Math.round(totalTraditionalIRAContributed),

      // Roth IRA
      valRothIRA: Math.round(valRothIRA),
      totalRothIRAContributed: Math.round(totalRothIRAContributed),

      // Totals
      totalPreTax: Math.round(totalPreTax),
      totalRoth: Math.round(totalRoth),
      totalRetirementSavings: Math.round(totalRetirementSavings),
      preTaxDrawdown: Math.round(preTaxDrawdown),
      rothDrawdown: Math.round(rothDrawdown),

      // Income breakdown
      pensionIncome: hasPension ? pensionAnnual : 0,
      socialSecurityIncome: socialSecurityIncome,
      totalAnnualIncome: Math.round(totalAnnualIncome),
      monthlyIncome: Math.round(monthlyIncome),

      // Income before Social Security Age (for early retirement)
      annualIncomeBeforeSocialSecurity: Math.round(annualIncomeBeforeSocialSecurity),
      monthlyIncomeBeforeSocialSecurity: Math.round(monthlyIncomeBeforeSocialSecurity),

      // Summary
      totalPersonalContributions: Math.round(totalPersonalContributions),
      totalEmployerContributions: Math.round(totalEmployerContributions),
      yearsToRetirement
    };
  };

  const results = calculateRetirement();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens AI</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-semibold">Complete US Retirement Planning Tool</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            US Retirement Calculator
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> (All Accounts)</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8">
            Plan your complete US retirement with 401(k), IRA, Roth IRA, Pension, and Social Security.
            Free, comprehensive, and easy to use.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Input Section */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Calculator className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Your Details</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Age
                    </label>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="18"
                      max="75"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Retirement Age
                    </label>
                    <input
                      type="number"
                      value={retirementAge}
                      onChange={(e) => setRetirementAge(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="55"
                      max="75"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Annual Return (%)
                  </label>
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="15"
                    step="0.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Conservative: 5-6% | Moderate: 7-8% | Aggressive: 9-10%
                  </p>
                </div>
              </div>
            </div>

            {/* 401(k) */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Briefcase className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-bold text-gray-900">401(k) Plan</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Balance ($)
                  </label>
                  <input
                    type="number"
                    value={current401k}
                    onChange={(e) => setCurrent401k(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Monthly ($)
                    </label>
                    <input
                      type="number"
                      value={monthly401kEmployee}
                      onChange={(e) => setMonthly401kEmployee(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min="0"
                      step="50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Employer Match ($)
                    </label>
                    <input
                      type="number"
                      value={monthly401kEmployer}
                      onChange={(e) => setMonthly401kEmployer(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min="0"
                      step="50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Traditional IRA */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Building2 className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Traditional IRA</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Balance ($)
                  </label>
                  <input
                    type="number"
                    value={currentTraditionalIRA}
                    onChange={(e) => setCurrentTraditionalIRA(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Contribution ($)
                  </label>
                  <input
                    type="number"
                    value={monthlyTraditionalIRA}
                    onChange={(e) => setMonthlyTraditionalIRA(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="50"
                  />
                </div>
              </div>
            </div>

            {/* Roth IRA */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Gift className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900">Roth IRA</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Balance ($)
                  </label>
                  <input
                    type="number"
                    value={currentRothIRA}
                    onChange={(e) => setCurrentRothIRA(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Contribution ($)
                  </label>
                  <input
                    type="number"
                    value={monthlyRothIRA}
                    onChange={(e) => setMonthlyRothIRA(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    min="0"
                    step="50"
                  />
                </div>
              </div>
            </div>

            {/* Pension */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Landmark className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-bold text-gray-900">Pension (Defined Benefit)</h3>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasPension}
                    onChange={(e) => setHasPension(e.target.checked)}
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Include</span>
                </label>
              </div>

              {hasPension && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Annual Income at Retirement ($)
                  </label>
                  <input
                    type="number"
                    value={pensionAnnual}
                    onChange={(e) => setPensionAnnual(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Check your pension statement for projected annual benefit
                  </p>
                </div>
              )}
            </div>

            {/* Social Security */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Target className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-900">Social Security</h3>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSocialSecurity}
                    onChange={(e) => setIncludeSocialSecurity(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Include</span>
                </label>
              </div>

              {includeSocialSecurity && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Social Security Claiming Age
                    </label>
                    <input
                      type="number"
                      value={socialSecurityAge}
                      onChange={(e) => setSocialSecurityAge(parseInt(e.target.value) || 67)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="62"
                      max="70"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Age 62: Early (reduced benefit) | Age 67: Full Retirement Age | Age 70: Maximum benefit
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Estimated Annual Benefit ($)
                    </label>
                    <input
                      type="number"
                      value={estimatedSocialSecurity}
                      onChange={(e) => setEstimatedSocialSecurity(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      step="1000"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Check your estimated benefit at ssa.gov/myaccount
                      <br />
                      Average benefit: ${AVERAGE_SOCIAL_SECURITY_ANNUAL.toLocaleString()}/year (~$1,907/month)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Total Retirement Income */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6" />
                <h3 className="text-xl font-bold">Retirement Income</h3>
              </div>

              {/* If retiring before Social Security Age */}
              {includeSocialSecurity && retirementAge < socialSecurityAge ? (
                <>
                  <div className="bg-white/10 rounded-lg p-4 mb-4">
                    <div className="text-sm text-blue-100 mb-2">Age {retirementAge} - {socialSecurityAge - 1} (Before Social Security)</div>
                    <div className="text-4xl font-bold mb-1">
                      ${results.monthlyIncomeBeforeSocialSecurity.toLocaleString()}/mo
                    </div>
                    <p className="text-blue-100 text-sm">
                      ${results.annualIncomeBeforeSocialSecurity.toLocaleString()}/year
                    </p>
                  </div>

                  <div className="bg-white/20 rounded-lg p-4 border-2 border-white/30">
                    <div className="text-sm text-blue-100 mb-2">Age {socialSecurityAge}+ (With Social Security)</div>
                    <div className="text-5xl font-bold mb-1">
                      ${results.monthlyIncome.toLocaleString()}/mo
                    </div>
                    <p className="text-blue-100 text-lg">
                      ${results.totalAnnualIncome.toLocaleString()}/year
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-5xl font-bold mb-2">
                    ${results.monthlyIncome.toLocaleString()}/mo
                  </div>
                  <p className="text-blue-100 text-lg">
                    ${results.totalAnnualIncome.toLocaleString()}/year in {results.yearsToRetirement} years
                  </p>
                  {includeSocialSecurity && retirementAge === socialSecurityAge && (
                    <p className="text-blue-100 text-sm mt-2">
                      ✓ Includes Social Security from age {socialSecurityAge}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Income Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Income Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">401(k) Drawdown</span>
                  <span className="font-bold text-purple-600">${Math.round((results.val401k * 0.04) / 12).toLocaleString()}/mo</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Traditional IRA</span>
                  <span className="font-bold text-blue-600">${Math.round((results.valTraditionalIRA * 0.04) / 12).toLocaleString()}/mo</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Roth IRA (Tax-Free)</span>
                  <span className="font-bold text-green-600">${Math.round((results.valRothIRA * 0.04) / 12).toLocaleString()}/mo</span>
                </div>
                {hasPension && (
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Pension</span>
                    <span className="font-bold text-amber-600">${Math.round(results.pensionIncome / 12).toLocaleString()}/mo</span>
                  </div>
                )}
                {includeSocialSecurity && (
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Social Security</span>
                    <span className="font-bold text-indigo-600">${Math.round(results.socialSecurityIncome / 12).toLocaleString()}/mo</span>
                  </div>
                )}
              </div>
            </div>

            {/* Account Balances */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Account Values at {retirementAge}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">401(k)</span>
                  <span className="font-semibold text-gray-900">${results.val401k.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Traditional IRA</span>
                  <span className="font-semibold text-gray-900">${results.valTraditionalIRA.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Roth IRA</span>
                  <span className="font-semibold text-gray-900">${results.valRothIRA.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Total Savings</span>
                  <span className="font-bold text-blue-600">${results.totalRetirementSavings.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Tax Benefits */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg p-6 border-2 border-green-200">
              <div className="flex items-center space-x-3 mb-3">
                <Gift className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">Roth IRA Tax-Free Growth</h3>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                ${results.valRothIRA.toLocaleString()}
              </div>
              <p className="text-sm text-gray-600">
                All Roth IRA withdrawals are 100% tax-free in retirement!
              </p>
            </div>

            {/* Contribution Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contribution Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Your Contributions</span>
                  <span className="font-semibold text-gray-900">${results.totalPersonalContributions.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Employer Contributions</span>
                  <span className="font-semibold text-green-600">+${results.totalEmployerContributions.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Investment Growth</span>
                  <span className="font-semibold text-blue-600">
                    ${(results.totalRetirementSavings - results.totalPersonalContributions - results.totalEmployerContributions).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Understanding US Retirement Accounts */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding US Retirement Accounts</h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Briefcase className="w-5 h-5 text-purple-600 mr-2" />
                  401(k) Plan
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Employer-sponsored retirement plan. Contributions reduce taxable income.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• 2024 limit: $23,000 ($30,500 if 50+)</li>
                  <li>• Employer match is free money</li>
                  <li>• Tax-deferred growth until withdrawal</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Building2 className="w-5 h-5 text-blue-600 mr-2" />
                  Traditional IRA
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Individual retirement account with tax-deductible contributions.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• 2024 limit: $7,000 ($8,000 if 50+)</li>
                  <li>• Tax deduction in contribution year</li>
                  <li>• Taxed upon withdrawal in retirement</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Gift className="w-5 h-5 text-green-600 mr-2" />
                  Roth IRA
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  After-tax contributions with tax-free withdrawals in retirement.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• 2024 limit: $7,000 ($8,000 if 50+)</li>
                  <li>• No tax deduction on contributions</li>
                  <li>• 100% tax-free growth and withdrawals</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Target className="w-5 h-5 text-indigo-600 mr-2" />
                  Social Security
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Government retirement benefit based on lifetime earnings.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• Full retirement age: 67 (for those born 1960+)</li>
                  <li>• Can claim as early as 62 (reduced benefit)</li>
                  <li>• Delay to 70 for maximum benefit</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 2024 Contribution Limits */}
          <div className="bg-blue-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2024 Contribution Limits</h2>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">401(k) Contribution Limit</h3>
                    <p className="text-sm text-gray-700">
                      $23,000 per year ($30,500 if age 50+)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">IRA Contribution Limit</h3>
                    <p className="text-sm text-gray-700">
                      $7,000 per year ($8,000 if age 50+) - applies to both Traditional and Roth combined
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Roth IRA Income Limits</h3>
                    <p className="text-sm text-gray-700">
                      Phase-out begins at $146,000 (single) or $230,000 (married filing jointly) for 2024
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Catch-Up Contributions</h3>
                    <p className="text-sm text-gray-700">
                      Age 50+: Extra $7,500 for 401(k), extra $1,000 for IRAs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Track with PlanLens */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <PieChart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Track All Your Retirement Accounts with PlanLens
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              This calculator gives you projections. PlanLens gives you the complete picture with
              real-time tracking, AI insights, and comprehensive retirement planning.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Track All Account Types</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">AI-Powered Insights</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Monte Carlo Simulations</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
            >
              Start Tracking Your Retirement Free
            </button>
          </div>
        </div>

        {/* Important Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Important Disclaimer</h3>
              <p className="text-sm text-gray-700 mb-2">
                This calculator provides estimates only and should not be considered financial advice.
                Actual returns will vary based on investment performance, fees, and market conditions.
              </p>
              <p className="text-sm text-gray-700">
                For personalized retirement advice, please consult a licensed financial advisor or Certified Financial Planner (CFP).
                Check your Social Security estimate at ssa.gov/myaccount
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © 2025 PlanLens. US Retirement Calculator for comprehensive planning.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default USRetirementCalculatorPage;
